import React from 'react';
import loadable from '@loadable/component';
import { pathToRegexp } from 'path-to-regexp';

import getPageDataLoadingAPI from '../containers/pageDataLoadingAPI';
import NotFoundPage from '../containers/NotFoundPage/NotFoundPage';

// routeConfiguration needs to initialize containers first
// Otherwise, components will import form container eventually and
// at that point css bundling / imports will happen in wrong order.
import { NamedRedirect } from '../components';
import {
  LISTING_TYPE__AIRPLANES,
  DRAFT_ID,
  DRAFT_SLUG,
} from '../util/editListingHelpers';
import {
  aircraftChildLandingPageService,
} from '../services/services';
import {
  getIsShowManageTeamNav,
} from '../util/subscription';
import {
  getIsBrokerAccount,
  getIsInactiveBroker,
} from '../util/user.path';

const pageDataLoadingAPI = getPageDataLoadingAPI();

const AboutPage = loadable(() => import(/* webpackChunkName: "AboutPage" */ '../containers/AboutPage/AboutPage'));
const AuthenticationPage = loadable(() => import(/* webpackChunkName: "AuthenticationPage" */ '../containers/AuthenticationPage/AuthenticationPage'));
const CheckoutPage = loadable(() => import(/* webpackChunkName: "CheckoutPage" */ '../containers/CheckoutPage/CheckoutPage'));
const ContactPage = loadable(() => import(/* webpackChunkName: "ContactPage" */ '../containers/ContactPage/ContactPage'));
const ContactPageSuccess = loadable(() => import(/* webpackChunkName: "ContactPageSuccess" */ '../containers/ContactPageSuccess/ContactPageSuccess'));
const ContactDetailsPage = loadable(() => import(/* webpackChunkName: "ContactDetailsPage" */ '../containers/ContactDetailsPage/ContactDetailsPage'));
const EditListingPage = loadable(() => import(/* webpackChunkName: "EditListingPage" */ '../containers/EditListingPage/EditListingPage'));
const EmailVerificationPage = loadable(() => import(/* webpackChunkName: "EmailVerificationPage" */ '../containers/EmailVerificationPage/EmailVerificationPage'));
const FavoritesPage = loadable(() => import(/* webpackChunkName: "FavoritesPage" */ '../containers/FavoritesPage/FavoritesPage'));
const InboxPage = loadable(() => import(/* webpackChunkName: "InboxPage" */ '../containers/InboxPage/InboxPage'));
const LandingPage = loadable(() => import(/* webpackChunkName: "LandingPage" */ '../containers/LandingPage/LandingPage'));
// const ListingPageHeroImage = loadable(() => import(/* webpackChunkName: "ListingPageHeroImage" */ /* webpackPrefetch: true */ '../containers/ListingPage/ListingPageHeroImage'));
// const ListingPageFullImage = loadable(() => import(/* webpackChunkName: "ListingPageFullImage" */ /* webpackPrefetch: true */ '../containers/ListingPage/ListingPageFullImage'));
const ManageListingsPage = loadable(() => import(/* webpackChunkName: "ManageListingsPage" */ '../containers/ManageListingsPage/ManageListingsPage'));
const PasswordChangePage = loadable(() => import(/* webpackChunkName: "PasswordChangePage" */ '../containers/PasswordChangePage/PasswordChangePage'));
const SubscriptionPage = loadable(() => import(/* webpackChunkName: "SubscriptionPage" */ '../containers/SubscriptionPage/SubscriptionPage'));
const PasswordRecoveryPage = loadable(() => import(/* webpackChunkName: "PasswordRecoveryPage" */ '../containers/PasswordRecoveryPage/PasswordRecoveryPage'));
const PasswordResetPage = loadable(() => import(/* webpackChunkName: "PasswordResetPage" */ '../containers/PasswordResetPage/PasswordResetPage'));
const PaymentMethodsPage = loadable(() => import(/* webpackChunkName: "PaymentMethodsPage" */ '../containers/PaymentMethodsPage/PaymentMethodsPage'));
const PrivacyPolicyPage = loadable(() => import(/* webpackChunkName: "PrivacyPolicyPage" */ '../containers/PrivacyPolicyPage/PrivacyPolicyPage'));
const ProfilePage = loadable(() => import(/* webpackChunkName: "ProfilePage" */ '../containers/ProfilePage/ProfilePage'));
const ProfileSettingsPage = loadable(() => import(/* webpackChunkName: "ProfileSettingsPage" */ '../containers/ProfileSettingsPage/ProfileSettingsPage'));
// const SearchPageWithMap = loadable(() => import(/* webpackChunkName: "SearchPageWithMap" */ /* webpackPrefetch: true */  '../containers/SearchPage/SearchPageWithMap'));
// const SearchPageWithList = loadable(() => import(/* webpackChunkName: "SearchPageWithList" */ /* webpackPrefetch: true */  '../containers/SearchPage/SearchPageWithList'));
const StripePayoutPage = loadable(() => import(/* webpackChunkName: "StripePayoutPage" */ '../containers/StripePayoutPage/StripePayoutPage'));
const TermsOfServicePage = loadable(() => import(/* webpackChunkName: "TermsOfServicePage" */ '../containers/TermsOfServicePage/TermsOfServicePage'));
const TransactionPage = loadable(() => import(/* webpackChunkName: "TransactionPage" */ '../containers/TransactionPage/TransactionPage'));
const SetNewListingTypePage = loadable(() => import(/* webpackChunkName: "SetNewListingTypePage" */ '../containers/SetNewListingTypePage/SetNewListingTypePage'));
const NotificationsPage = loadable(() => import(/* webpackChunkName: "NotificationsPage" */ '../containers/NotificationsPage/NotificationsPage'));
const SavedSearchPage = loadable(() => import(/* webpackChunkName: "SavedSearchPage" */ '../containers/SavedSearchPage/SavedSearchPage'));

// Styleguide helps you to review current components and develop new ones
const StyleguidePage = loadable(() => import(/* webpackChunkName: "StyleguidePage" */ '../containers/StyleguidePage/StyleguidePage'));

// const SearchPage = config.searchPageVariant === 'map' ? SearchPageWithMap : SearchPageWithList;
const AircraftSearchPage = loadable(() => import(/* webpackChunkName: "AircraftSearchPage" */ /* webpackPrefetch: true */  '../containers/SearchPage/AircraftSearchPage/AircraftSearchPage'));
const RealEstateSearchPage = loadable(() => import(/* webpackChunkName: "RealEstateSearchPage" */ /* webpackPrefetch: true */  '../containers/SearchPage/RealEstateSearchPage/RealEstateSearchPage'));
// const ListingPage = config.listingPageLayout === 'full-image' ? ListingPageFullImage : ListingPageHeroImage;
const ListingPage = loadable(() => import(/* webpackChunkName: "ListingPage" */ '../containers/ListingPage/ListingPage'));
const UsersPage = loadable(() => import(/* webpackChunkName: "UsersPage" */ '../containers/UsersPage/UsersPage'));
const AircraftChildLandingPage = loadable(() => import(/* webpackChunkName: "AircraftChildLandingPage" */ '../containers/AircraftChildLandingPage/AircraftChildLandingPage'));
const KeepListingAvailablePage = loadable(() => import(/* webpackChunkName: "KeepListingAvailablePage" */ '../containers/KeepListingAvailablePage/KeepListingAvailablePage'));
const UnderReviewPage = loadable(() => import(/* webpackChunkName: "UnderReviewPage" */ '../containers/UnderReviewPage/UnderReviewPage'));
const AircraftRegistrationPage = loadable(() => import(/* webpackChunkName: "AircraftRegistrationPage" */ '../containers/AircraftRegistrationPage/AircraftRegistrationPage'));
const RegistrationPhotosPage = loadable(() => import(/* webpackChunkName: "RegistrationPhotosPage" */ '../containers/RegistrationPhotosPage/RegistrationPhotosPage'));
const AircraftRegistrationsPage = loadable(() => import(/* webpackChunkName: "AircraftRegistrationsPage" */ '../containers/AircraftRegistrationsPage/AircraftRegistrationsPage'));
const AircraftParentLandingPage = loadable(() => import(/* webpackChunkName: "AircraftParentLandingPage" */ '../containers/AircraftParentLandingPage/AircraftParentLandingPage'));
const PaymentResponsePage = loadable(() => import(/* webpackChunkName: "PaymentResponsePage" */ '../containers/PaymentResponsePage/PaymentResponsePage'));
const AirPricingPage = loadable(() => import(/* webpackChunkName: "AirPricingPage" */ '../containers/AirPricingPage/AirPricingPage'));
const ManageTeamPage =  loadable(() =>  import(/* webpackChunkName: "ManageTeamPage" */ '../containers/ManageTeamPage/ManageTeamPage'));
const SubscribeResponsePage = loadable(() => import(/* webpackChunkName: "SubscribeResponsePage" */ '../containers/SubscribeResponsePage/SubscribeResponsePage'));
const DashboardPage = loadable(() => import(/* webpackChunkName: "DashboardPage" */ '../containers/DashboardPage/DashboardPage'));
const BrokerLimitsPage = loadable(() => import(/* webpackChunkName: "BrokerLimitsPage" */ '../containers/BrokerLimitsPage/BrokerLimitsPage'));
const BuyersPage = loadable(() => import(/* webpackChunkName: "BuyersPage" */ '../containers/BuyersPage/BuyersPage'));
const DealerPackagesPage = loadable(() => import(/* webpackChunkName: "DealerPackagesPage" */ '../containers/DealerPackagesPage/DealerPackagesPage'));
const SellerPage = loadable(() => import(/* webpackChunkName: "SellerPage" */ '../containers/SellerPage/SellerPage'));

export const ACCOUNT_SETTINGS_PAGES = [
  'ContactDetailsPage',
  'PasswordChangePage',
  'SubscriptionPage',
  'StripePayoutPage',
  'PaymentMethodsPage',
];

// https://en.wikipedia.org/wiki/Universally_unique_identifier#Nil_UUID
const draftId = '00000000-0000-0000-0000-000000000000';
const draftSlug = 'draft';

const RedirectToLandingPage = () => <NamedRedirect name="LandingPage" />;

// NOTE: Most server-side endpoints are prefixed with /api. Requests to those
// endpoints are indended to be handled in the server instead of the browser and
// they will not render the application. So remember to avoid routes starting
// with /api and if you encounter clashing routes see server/index.js if there's
// a conflicting route defined there.

// Our routes are exact by default.
// See behaviour from Routes.js where Route is created.
const routeConfiguration = () => {
  return [
    {
      path: '/',
      name: 'LandingPage',
      component: LandingPage,
      loadData: pageDataLoadingAPI.LandingPage.loadData,
    },
    {
      path: '/about',
      name: 'AboutPage',
      component: AboutPage,
    },
    {
      path: '/contact',
      name: 'ContactPage',
      component: ContactPage,
    },
    {
      path: '/contactSuccess',
      name: 'ContactPageSuccess',
      component: ContactPageSuccess,
    },
    {
      path: '/watchlist',
      name: 'FavoritesPage',
      auth: true,
      authPage: 'LoginPage',
      component: FavoritesPage,
      loadData: pageDataLoadingAPI.FavoritesPage.loadData,
    },
    {
      path: '/registrations_photos',
      name: 'RegistrationPhotosPage',
      auth: true,
      authPage: 'LoginPage',
      component: RegistrationPhotosPage,
      loadData: pageDataLoadingAPI.RegistrationPhotosPage.loadData,
    },
    {
      path: pathToRegexp('/aircraft/uncategorized'),
      name: 'UncategorizedNotFoundPage',
      component: props => <NotFoundPage {...props} />,
    },
    {
      path: '/aircraft/uncategorized/:id([0-9a-fA-F]{8}\\b-[0-9a-fA-F]{4}\\b-[0-9a-fA-F]{4}\\b-[0-9a-fA-F]{4}\\b-[0-9a-fA-F]{12})',
      name: 'AircraftListingPage',
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
    },
    {
      path: '/real_estate',
      name: 'RealEstateSearchPage',
      component: RealEstateSearchPage,
      loadData: pageDataLoadingAPI.SearchPage.loadRealEstateData,
    },
    {
      path: '/real_estate/:id',
      name: 'RealEstateListingPage',
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
    },
    {
      path: '/real_estate/:id/under-review',
      name: 'UnderReviewPage',
      component: UnderReviewPage,
    },
    {
      path: '/aircraft/:category/:maker/:modelFamily/:model/:id',
      name: 'AircraftListingPageCanonical',
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
    },
    {
      path: '/aircraft/:id([0-9a-fA-F]{8}\\b-[0-9a-fA-F]{4}\\b-[0-9a-fA-F]{4}\\b-[0-9a-fA-F]{4}\\b-[0-9a-fA-F]{12})',
      name: 'AircraftListingPageCanonicalShort',
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
    },
    {
      path: '/aircraft',
      name: 'AircraftSearchPage',
      component: AircraftSearchPage,
      loadData: pageDataLoadingAPI.SearchPage.loadData,
    },
    {
      path: '/aircraft/:can_category',
      name: 'AircraftChildLandingPageWithCategory',
      component: AircraftChildLandingPage,
      loadData: aircraftChildLandingPageService.loadData,
    },
    {
      path: '/aircraft/:can_category/:can_maker',
      name: 'AircraftChildLandingPageWithMaker',
      component: AircraftChildLandingPage,
      loadData: aircraftChildLandingPageService.loadData,
    },
    {
      path: '/aircraft/:can_category/:can_maker/:can_model_family',
      name: 'AircraftChildLandingPageWithModelFamily',
      component: AircraftChildLandingPage,
      loadData: aircraftChildLandingPageService.loadData,
    },
    {
      path: '/aircraft/:can_category/:can_maker/:can_model_family/:can_model',
      name: 'AircraftChildLandingPageWithModel',
      component: AircraftChildLandingPage,
      loadData: aircraftChildLandingPageService.loadData,
    },
    {
      path: '/aircraft-category',
      name: 'AircraftParentLandingPage',
      component: AircraftParentLandingPage,
      loadData: pageDataLoadingAPI.AircraftParentLandingPage.loadData,
    },
    {
      path: '/users',
      name: 'UsersPage',
      component: UsersPage,
      loadData: pageDataLoadingAPI.UsersPage.loadData,
    },
    {
      path: '/l',
      name: 'ListingBasePage',
      component: RedirectToLandingPage,
    },
    {
      path: '/l/:slug/:id',
      name: 'ListingPage',
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
    },
    {
      path: '/l/:slug/:id/checkout',
      name: 'CheckoutPage',
      auth: true,
      component: CheckoutPage,
      setInitialValues: pageDataLoadingAPI.CheckoutPage.setInitialValues,
    },
    {
      path: '/l/:slug/:id/:variant',
      name: 'ListingPageVariant',
      auth: true,
      authPage: 'LoginPage',
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
    },
    {
      path: '/l/type',
      name: 'SetNewListingTypePage',
      auth: true,
      component: SetNewListingTypePage,
    },
    {
      path: '/l/new',
      name: 'NewListingPage',
      auth: true,
      component: () => (
        <NamedRedirect
          name="EditListingPage"
          params={{
            slug: DRAFT_SLUG,
            id: DRAFT_ID,
            type: 'new',
            tab: 'details',
            listingType: LISTING_TYPE__AIRPLANES,
        }}
        />
      ),
    },
    {
      path: '/l/:slug/:id/:type/:tab/:listingType',
      name: 'EditListingPage',
      auth: true,
      checkLimits: (match, subscriptionInfo, currentUser) => {
        const isInactiveBroker = getIsInactiveBroker(currentUser);
        const is = isInactiveBroker
          ? false
          : (match.params.type === 'new' && match.params.listingType === LISTING_TYPE__AIRPLANES)
            ? subscriptionInfo.limits.canCreateAirListing
            : true
        ;
        const redirectPage = isInactiveBroker ? 'BrokerLimitsPage' : 'AirPricingPage';
        return {
          is,
          redirectPage,
        }
      },
      component: EditListingPage,
      loadData: pageDataLoadingAPI.EditListingPage.loadData,
    },
    {
      path: '/l/:slug/:id/:type/:tab/:listingType/:returnURLType',
      name: 'EditListingStripeOnboardingPage',
      auth: true,
      component: EditListingPage,
      loadData: pageDataLoadingAPI.EditListingPage.loadData,
    },

    // Canonical path should be after the `/l/new` path since they
    // conflict and `new` is not a valid listing UUID.
    {
      path: '/l/:id',
      name: 'ListingPageCanonical',
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
    },
    {
      path: '/u',
      name: 'ProfileBasePage',
      component: RedirectToLandingPage,
    },
    {
      path: '/u/:id',
      name: 'ProfilePage',
      component: ProfilePage,
      loadData: pageDataLoadingAPI.ProfilePage.loadData,
    },
    {
      path: '/profile-settings',
      name: 'ProfileSettingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: ProfileSettingsPage,
    },
    {
      path: '/dashboard',
      name: 'DashboardPage',
      component: DashboardPage,
    },
    {
      path: '/manage-team',
      auth: true,
      name: 'ManageTeamPage',
      component: ManageTeamPage,
      checkLimits: (match, subscriptionInfo, currentUser) => {
        return {
          is: getIsShowManageTeamNav(currentUser, subscriptionInfo.entitlements),
          redirectPage: 'AirPricingPage',
        };
      },
    },

    // Note: authenticating with IdP (e.g. Facebook) expects that /login path exists
    // so that in the error case users can be redirected back to the LoginPage
    // In case you change this, remember to update the route in server/api/auth/loginWithIdp.js
    {
      path: '/login',
      name: 'LoginPage',
      component: AuthenticationPage,
      extraProps: { tab: 'login' },
    },
    {
      path: '/signup',
      name: 'SignupPage',
      component: AuthenticationPage,
      extraProps: { tab: 'signup' },
    },
    {
      path: '/signup-broker',
      name: 'SignupPage',
      component: AuthenticationPage,
      extraProps: { tab: 'signup' },
    },
    {
      path: '/confirm',
      name: 'ConfirmPage',
      component: AuthenticationPage,
      extraProps: { tab: 'confirm' },
    },
    {
      path: '/recover-password',
      name: 'PasswordRecoveryPage',
      component: PasswordRecoveryPage,
    },
    {
      path: '/inbox',
      name: 'InboxBasePage',
      auth: true,
      authPage: 'LoginPage',
      component: () => <NamedRedirect name="InboxPage" params={{ tab: 'sales' }} />,
    },
    {
      path: '/inbox/:tab',
      name: 'InboxPage',
      auth: true,
      authPage: 'LoginPage',
      component: InboxPage,
      loadData: pageDataLoadingAPI.InboxPage.loadData,
    },
    {
      path: '/order/:id',
      name: 'OrderPage',
      auth: true,
      authPage: 'LoginPage',
      component: props => <NamedRedirect name="OrderDetailsPage" params={{ ...props.params }} />,
    },
    {
      path: '/order/:id/details',
      name: 'OrderDetailsPage',
      auth: true,
      authPage: 'LoginPage',
      component: TransactionPage,
      extraProps: { transactionRole: 'customer' },
      loadData: params =>
        pageDataLoadingAPI.TransactionPage.loadData({ ...params, transactionRole: 'customer' }),
      setInitialValues: pageDataLoadingAPI.TransactionPage.setInitialValues,
    },
    {
      path: '/sale/:id',
      name: 'SalePage',
      auth: true,
      authPage: 'LoginPage',
      component: props => <NamedRedirect name="SaleDetailsPage" params={{ ...props.params }} />,
    },
    {
      path: '/sale/:id/details',
      name: 'SaleDetailsPage',
      auth: true,
      authPage: 'LoginPage',
      component: TransactionPage,
      extraProps: { transactionRole: 'provider' },
      loadData: params =>
        pageDataLoadingAPI.TransactionPage.loadData({ ...params, transactionRole: 'provider' }),
    },
    {
      path: '/listings',
      name: 'ManageListingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: ManageListingsPage,
    },
    {
      path: '/account',
      name: 'AccountSettingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: () => <NamedRedirect name="ContactDetailsPage" />,
    },
    {
      path: '/account/contact-details',
      name: 'ContactDetailsPage',
      auth: true,
      authPage: 'LoginPage',
      component: ContactDetailsPage,
      loadData: pageDataLoadingAPI.ContactDetailsPage.loadData,
    },
    {
      path: '/account/change-password',
      name: 'PasswordChangePage',
      auth: true,
      authPage: 'LoginPage',
      component: PasswordChangePage,
    },
    {
      path: '/account/subscription',
      name: 'SubscriptionPage',
      auth: true,
      authPage: 'LoginPage',
      component: SubscriptionPage,
      checkLimits: (match, subscriptionInfo, currentUser) => {
        const isInactiveBroker = getIsInactiveBroker(currentUser);
        const is = isInactiveBroker
          ? false
          : !getIsBrokerAccount(currentUser)
        ;
        const redirectPage = isInactiveBroker ? 'BrokerLimitsPage' : 'AirPricingPage';
        return {
          is,
          redirectPage,
        };
      },
    },
    {
      path: '/account/payments',
      name: 'StripePayoutPage',
      auth: true,
      authPage: 'LoginPage',
      component: StripePayoutPage,
      loadData: pageDataLoadingAPI.StripePayoutPage.loadData,
    },
    {
      path: '/account/payments/:returnURLType',
      name: 'StripePayoutOnboardingPage',
      auth: true,
      authPage: 'LoginPage',
      component: StripePayoutPage,
      loadData: pageDataLoadingAPI.StripePayoutPage.loadData,
    },
    {
      path: '/account/payment-methods',
      name: 'PaymentMethodsPage',
      auth: true,
      authPage: 'LoginPage',
      component: PaymentMethodsPage,
      loadData: pageDataLoadingAPI.PaymentMethodsPage.loadData,
    },
    {
      path: '/terms-of-use',
      name: 'TermsOfServicePage',
      component: TermsOfServicePage,
    },
    {
      path: '/privacy-policy',
      name: 'PrivacyPolicyPage',
      component: PrivacyPolicyPage,
    },
    {
      path: '/styleguide',
      name: 'Styleguide',
      component: StyleguidePage,
    },
    {
      path: '/styleguide/g/:group',
      name: 'StyleguideGroup',
      component: StyleguidePage,
    },
    {
      path: '/styleguide/c/:component',
      name: 'StyleguideComponent',
      component: StyleguidePage,
    },
    {
      path: '/styleguide/c/:component/:example',
      name: 'StyleguideComponentExample',
      component: StyleguidePage,
    },
    {
      path: '/styleguide/c/:component/:example/raw',
      name: 'StyleguideComponentExampleRaw',
      component: StyleguidePage,
      extraProps: { raw: true },
    },
    {
      path: '/notfound',
      name: 'NotFoundPage',
      component: props => <NotFoundPage {...props} />,
    },

    // Do not change this path!
    //
    // The API expects that the application implements /reset-password endpoint
    {
      path: '/reset-password',
      name: 'PasswordResetPage',
      component: PasswordResetPage ,
    },
    {
      path: '/keep_listing_available',
      name: 'KeepListingAvailablePage',
      component: KeepListingAvailablePage,
    },
    // Do not change this path!
    //
    // The API expects that the application implements /verify-email endpoint
    {
      path: '/verify-email',
      name: 'EmailVerificationPage',
      auth: true,
      authPage: 'LoginPage',
      component: EmailVerificationPage,
      loadData: pageDataLoadingAPI.EmailVerificationPage.loadData,
    },
    {
      path: '/notifications',
      name: 'NotificationsPage',
      auth: true,
      authPage: 'LoginPage',
      component: NotificationsPage,
    },
    {
      path: '/savedSearch',
      name: 'SavedSearchPage',
      auth: true,
      authPage: 'LoginPage',
      component: SavedSearchPage,
    },
    {
      path: '/registration/:reg_no',
      name: 'AircraftRegistrationPage',
      component: AircraftRegistrationPage,
      loadData: pageDataLoadingAPI.AircraftRegistrationPage.loadData,
    },
    {
      path: '/registration',
      name: 'AircraftRegistrationsPage',
      component: AircraftRegistrationsPage,
      loadData: pageDataLoadingAPI.AircraftRegistrationsPage.loadData,
    },
    {
      path: '/payment-response',
      name: 'PaymentResponsePage',
      component: PaymentResponsePage,
    },
    {
      path: '/pricing',
      name: 'AirPricingPage',
      component: AirPricingPage,
      loadData: pageDataLoadingAPI.AirPricingPage.loadData,
      checkLimits: (match, subscriptionInfo, currentUser) => {
        return {
          is: !getIsInactiveBroker(currentUser),
          redirectPage: 'BrokerLimitsPage',
        }
      },
    },
    {
      path: '/subscribe-response',
      name: 'SubscribeResponsePage',
      component: SubscribeResponsePage,
    },
    {
      path: '/broker-limits',
      name: 'BrokerLimitsPage',
      component: BrokerLimitsPage,
    },
    {
      path: '/buyers',
      name: 'BuyersPage',
      component: BuyersPage,
    },
    {
      path: '/dealer-packages',
      name: 'DealerPackagesPage',
      component: DealerPackagesPage,
    },
    {
      path: '/seller',
      name: 'SellerPage',
      component: SellerPage,
    },
  ];
};

export default routeConfiguration;
